import clsx from 'clsx'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import Group from 'components/utils/Group'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import ExperienceDetailsAboutLocationMap from '../ExperienceDetailsModal/About/ExperienceDetailsAboutLocationMap'
import TextButton from 'LuxKit/Button/TextButton'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { fetchTransferOptions } from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import ExperienceTransferMultipleBooking from './ExperienceTransferMultipleBooking'
import ExperienceTransferSingleBooking from './ExperienceTransferSingleBooking'
import { rem } from 'polished'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import Typography from 'LuxKit/Typography/Typography'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import ClockFiveIcon from 'LuxKit/Icons/line/LineClockFiveIcon'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Divider from 'LuxKit/Divider'
import Accordion from 'LuxKit/Accordion'
import { MAP_TITLE_TO_EVENT } from 'components/Experiences/ExperienceOfferPage/ExperienceOfferPageContent'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { getExperienceDurationString } from 'lib/experiences/experienceUtils'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import { getHasLuxPlusOrSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscription'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import ExperienceBookingModalPrices from '../ExperienceBookingModalPrices'
import { generateLoyaltyPointsCalculatorTransfersOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LayoutContainer from 'components/Common/LayoutContainer'

const Footer = styled.div`
  display: flex;
  width: 100%;
  gap: ${rem(12)};
  justify-content: space-between;

  flex-direction: column;

  >:last-child {
    width: 100%;
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;

    >:last-child {
      width: auto;
    }
  }
`

const FooterPrice = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const FadingElement = styled.div`
  display: none;
  min-width: ${rem(120)};

  &.visible {
    display: block;
  }

  ${mediaQueryUp.tablet} {
    display: block;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;

    &.visible {
      display: block;
      opacity: 1;
      pointer-events: auto;
    }
  }
`

const SubmitButtonContainer = styled.div`
  min-width: ${rem(120)};
`

const ImageContainer = styled.div`
  height: ${rem(280)};

  & > * {
    height: 100%;
    width: 100%;
  }
`

export interface TransferBookingModalResolve {
  remove?: boolean
  transfers?: Array<App.ExperienceTransferView>
}

interface Props {
  experience: App.ExperienceBookingView
  defaultTransfer?: App.ExperienceTransferView
  defaultToAirportTransfer?: App.ExperienceTransferView
  defaultToHotelTransfer?: App.ExperienceTransferView
  hasLoyaltyBenefit?: boolean
  isLuxPlusEnabled: boolean
}

function ExperienceTransferBookingModal(props: Props) {
  const { experience: experienceView, defaultTransfer, defaultToAirportTransfer, defaultToHotelTransfer, hasLoyaltyBenefit, isLuxPlusEnabled } = props
  const modalElement = useModalElementContext<{ transfers?: Array<App.ExperienceTransferView>, remove?: boolean }>()
  const intl = useIntl()
  const totalSaleUnitLabel = intl.formatMessage({
    id: 'p5YNRv2X3_8SQE_O7g6-',
    defaultMessage: 'total',
    description: 'Default sale unit label for pricing in transfer booking modal footer',
  })
  const [price, setPrice] = useState<{ total: number, totalMember: number, totalGrossPrice: number, isFromPrice: boolean }>({ total: 0, totalMember: 0, totalGrossPrice: 0, isFromPrice: true })
  const formId = useId()
  const trackEvent = useTrackExperienceOfferEvent()
  const experience = experienceView.originalExperience

  const dispatch = useAppDispatch()

  const transferOptions = useAppSelector(state => state.experience.experienceTransferOptions[experienceView.id]?.transfers)

  useEffect(() => {
    dispatch(fetchTransferOptions(experienceView.id))
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceView.id])

  const onSubmit = useCallback((transfers: Array<App.ExperienceTransferView>) => {
    modalElement.resolve({ transfers })
  }, [modalElement])

  const onRemove = () => {
    modalElement.resolve({ remove: true })
  }

  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])

  const hasDescription = useMemo(
    () => experience.copy.dealDescription && experience.copy.dealDescription.trim() !== experienceView.title.trim(),
    [experience.copy.dealDescription, experienceView.title],
  )

  const onPriceChange = useCallback((total: number, totalMember: number, totalGrossPrice: number, isFromPrice: boolean) => setPrice({ total, totalMember, totalGrossPrice, isFromPrice }), [])
  const canRemove = !!(defaultTransfer?.option ?? defaultToAirportTransfer?.option ?? defaultToHotelTransfer?.option)

  const handleThingsToKnowOpen = useCallback((title: string) => {
    if (MAP_TITLE_TO_EVENT[title].open) {
      trackEvent(MAP_TITLE_TO_EVENT[title].open)
    }
  }, [trackEvent])

  const handleThingsToKnowClose = useCallback((title: string) => {
    if (MAP_TITLE_TO_EVENT[title].close) {
      trackEvent(MAP_TITLE_TO_EVENT[title].close)
    }
  }, [trackEvent])

  const pointsEarnCalculationRequest = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorTransfersOptions({
      price: price.total,
      memberPrice: price.totalMember,
      skip: !transferOptions,
    }),
  ], [price, transferOptions])

  const durationString = useMemo(() => getExperienceDurationString(experience, intl), [experience, intl])

  const updateLabel = intl.formatMessage({
    id: 'anlMHeGf0oX0UiXCccNO',
    defaultMessage: 'Update',
    description: 'Button label to update an existing transfer booking',
  })
  const addLabel = intl.formatMessage({
    id: 'atgQld7L6ZbP-ghPi-4N',
    defaultMessage: 'Add',
    description: 'Button label to add a new transfer booking in the booking flow',
  })

  return <ModalBase size="L" height="max">
    <ModalHeader title="" isOverlay />
    <ModalBody>
      <ImageContainer>
        <ImageGallery
          size="small"
          images={experience.images}
          showImageTitles={false}
        />
      </ImageContainer>
      <ModalContent>
        <Group direction="vertical" gap={20} tabletGap={24}>
          <Group direction="vertical" gap={8}>
            <Typography wrap="truncate">
              {experienceView.location && <>
                <Caption as="span" variant="large" colour="neutral-one" weight="normal">{experienceView.location}</Caption>
                <Caption as="span" variant="large" colour="neutral-one" weight="normal">{formatMiddleDotSeparator(intl)}</Caption>
              </>}
              <BodyText as="span" variant="small" colour="neutral-two">{experienceView.primaryCategory}</BodyText>
            </Typography>

            <Heading as="h3" variant="heading3">{experienceView.title}</Heading>

            <BodyTextBlock variant="medium" startIcon={<CalendarIcon aria-hidden="true" />}>
              <BodyText variant="medium">
                <BodyText variant="medium" weight="bold" as="span">
                  {intl.formatMessage({
                    id: 'Y6eAlH1EVHaqz2FRYhl4',
                    defaultMessage: 'Availability:',
                    description: 'Label for the availability information of a transfer experience',
                  })}
                </BodyText> {experience.copy.availability}
              </BodyText>
            </BodyTextBlock>

            <BodyTextBlock variant="medium" startIcon={<ClockFiveIcon aria-hidden="true" />}>
              <BodyText variant="medium">
                <BodyText variant="medium" weight="bold" as="span">
                  {intl.formatMessage({
                    id: 'oMKGcnxCAhRaQSCFXFfN',
                    defaultMessage: 'Duration:',
                    description: 'Form field label for the duration of a transfer experience',
                  })}
                </BodyText> {durationString}
              </BodyText>
            </BodyTextBlock>

            <ExperienceCancellationText
              cancellationInfo={experience.copy.cancellationInfo}
            />
            {hasDescription && <LargeContentToggle
              maxHeight={80}
              showMoreText={intl.formatMessage({
                id: 'rDC7UCOK2GCfUfrKK7Gv',
                defaultMessage: 'Read more',
                description: 'Button to expand the full description of a transfer experience',
              })}
              showLessText={intl.formatMessage({
                id: 'a_qEkHD8PjmYTtUuWL5e',
                defaultMessage: 'Read less',
                description: 'Button to collapse the description of a transfer experience',
              })}
            >
              <MarkdownRender content={experience.copy.dealDescription ?? ''} />
            </LargeContentToggle>}
          </Group>
          <Divider kind="primary" />
          {experience.copy.included?.trim() &&
            <>
              <div>
                <LargeContentToggle
                  maxHeight={80}
                  showMoreText={intl.formatMessage({
                    id: 'QU0aliBoEN2unh9JOaVy',
                    defaultMessage: 'View more inclusions',
                    description: 'Button to expand the full inclusions list for a transfer experience',
                  })}
                  showLessText={intl.formatMessage({
                    id: 'tNnZIy8e97vYy5YfhuIJ',
                    defaultMessage: 'View less inclusions',
                    description: 'Button to collapse the inclusions list for a transfer experience',
                  })}
                >
                  <BodyText as="h6" variant="large" weight="bold">
                    {intl.formatMessage({
                      id: 'aUlXKA9sYR3qrf4Eys3j',
                      defaultMessage: 'Inclusions',
                      description: 'Heading for the inclusions section of a transfer experience',
                    })}
                  </BodyText>
                  <MarkdownRender type="compact" content={experience.copy.included} />
                </LargeContentToggle>
              </div>
              <Divider kind="primary" />
            </>}
          {!transferOptions && <div>
            <LoadingIndicator inline visible>
              {intl.formatMessage({
                id: 'YIG-tYr15e7OVUEo6aqX',
                defaultMessage: 'Finding available transfers...',
                description: 'Loading message while transfer options are being fetched',
              })}
            </LoadingIndicator>
          </div>}
          {transferOptions && <>
            {defaultTransfer && <ExperienceTransferSingleBooking
              defaultTransfer={defaultTransfer}
              experience={experienceView}
              transferOptions={transferOptions}
              hasLoyaltyBenefit={hasLoyaltyBenefit}
              onPriceChange={onPriceChange}
              onSubmit={onSubmit}
              formId={formId}
            />}
            {!defaultTransfer && <ExperienceTransferMultipleBooking
              defaultToHotelTransfer={defaultToHotelTransfer}
              defaultToAirportTransfer={defaultToAirportTransfer}
              experience={experienceView}
              transferOptions={transferOptions}
              hasLoyaltyBenefit={hasLoyaltyBenefit}
              onPriceChange={onPriceChange}
              onSubmit={onSubmit}
              formId={formId}
            />}
          </>}
          <Divider kind="primary" />
          <div>
            <Accordion
              heading={intl.formatMessage({
                id: 'Fp3ZCgR6Pe8msupx87N9',
                defaultMessage: 'Location',
                description: 'Accordion heading for the location map section of a transfer experience',
              })}
              size="large"
              hideDivider
            >
              <ExperienceDetailsAboutLocationMap
                experienceView={experienceView}
              />
            </Accordion>
            {experience.copy.thingsToKnow.map(item => (
              <Accordion
                key={item.title}
                onOpen={() => handleThingsToKnowOpen(item.title)}
                onClose={() => handleThingsToKnowClose(item.title)}
                heading={item.title}
                size="large"
                hideDivider
              >
                <LayoutContainer>
                  <MarkdownRender content={item.content} type="compact" fontSize="medium" />
                </LayoutContainer>
              </Accordion>
            ))}
          </div>
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter>
      <Footer>
        <FooterPrice>
          {hasLoyaltyBenefit && <PriceRowPrice size="L" price={0} saleUnit={totalSaleUnitLabel} />}
          {!hasLoyaltyBenefit && <Group direction="vertical" gap={8}>
            <ExperienceBookingModalPrices
              totalPrice={price.total}
              totalMemberPrice={price.totalMember}
              totalGrossPrice={price.totalGrossPrice}
              isFromPrice={price.isFromPrice}
              ticketUnitLabel={price.isFromPrice && experienceView.ticketUnitLabel ? experienceView.ticketUnitLabel : totalSaleUnitLabel}
              discountPercentage={price.isFromPrice && experienceView.discount >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW ? experienceView.discount : undefined}
              isLuxPlusOffer={isLuxPlusEnabled && experienceView.pricing.memberPrice > 0}
            />
            <PriceRowAgentHubCommission
              offerId={experience.id}
              productType={productType}
              offerLocations={[experience.location.name]}
            />
            <LoyaltyPointsEarnWithBreakdownModal
              calculationRequests={pointsEarnCalculationRequest}
              calculationType={price.isFromPrice ? 'estimate' : 'exact'}
              hideStatusCredits
            />
          </Group>}
        </FooterPrice>
        <Group direction="vertical" tabletDirection="horizontal" gap={8}>
          <FadingElement
            as={TextButton}
            aria-hidden={!canRemove}
            className={clsx({ visible: canRemove })}
            kind="tertiary"
            size="large"
            onClick={onRemove}
          >
            {intl.formatMessage({
              id: 'lhJ0Cs5mC1H96FzqV2QE',
              defaultMessage: 'Remove',
              description: 'Button to remove a transfer booking in the booking or order summary view',
            })}
          </FadingElement>
          <SubmitButtonContainer>
            <TextButton
              kind="primary"
              size="large"
              type="submit"
              fit="full-width"
              form={formId}
            >
              {canRemove ? updateLabel : addLabel}
            </TextButton>
          </SubmitButtonContainer>
        </Group>
      </Footer>
    </ModalFooter>
  </ModalBase>
}

const mapStateToProps = (state: App.State) => {
  return {
    isLuxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state) && getHasLuxPlusOrSubscriptionInCart(state),
  }
}

export default connect(mapStateToProps)(ExperienceTransferBookingModal)
